import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { InvoiceSummaryDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core';
import { Button } from '@/components/ui/button';
import { Toast } from '@/components/ui/toast';
import { getInvoiceUrl } from '@/services/platform/data/orders';

interface ActivityInvoiceButtonProps {
  /** The owning order's public GUID. */
  orderId?: string | undefined;
  /** The invoice carried by the order. The button renders only when this is present. */
  invoice?: InvoiceSummaryDto | undefined;
}

/**
 * "Get your invoice" action for the activity detail screens.
 *
 * Shown whenever the order carries an invoice, regardless of its `status`: the PDF is generated
 * asynchronously, so an invoice that exists but is not yet `Available` is a normal, temporary
 * state that the user is told about rather than hidden from.
 *
 * Shared by both the order and reservation variants of the screen, which resolve the order GUID
 * differently but behave identically from the tap onwards.
 */
export function ActivityInvoiceButton({ orderId, invoice }: ActivityInvoiceButtonProps) {
  const invoiceId = invoice?.publicGuid;

  // Both GUIDs are needed to build the request URL, so anything less renders nothing at all. The
  // gate is a separate component from the button so the state below is only ever mounted when
  // there is something to download — and so the guard does not sit above a pile of hooks.
  if (!orderId || !invoiceId) return null;

  return <InvoiceDownloadButton orderId={orderId} invoiceId={invoiceId} />;
}

function InvoiceDownloadButton({ orderId, invoiceId }: { orderId: string; invoiceId: string }) {
  const t = useClientTranslation();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Remounts the toast so a repeated tap restarts its auto-dismiss timer instead of inheriting
  // the tail of the previous one. Same approach as the payment methods list.
  const [toastKey, setToastKey] = useState(0);

  function showToast(message: string) {
    setToast(message);
    setToastKey(key => key + 1);
  }

  async function handleClick() {
    // The SAS URL is minted per request, so a double tap costs a wasted round trip.
    if (loading) return;
    setLoading(true);

    // The tab must be opened synchronously, while the click is still the active user gesture.
    // By the time the URL arrives the gesture has expired and Safari blocks `window.open` as a
    // popup, so a blank tab is parked now and pointed at the PDF once the URL resolves.
    //
    // Deliberately no `noopener`/`noreferrer` here: either one makes `window.open` return `null`
    // by spec — the whole point of `noopener` is that the caller gets no handle on the new window.
    // That left the parked tab stranded on about:blank with nothing able to navigate it. The
    // opener is severed below instead, which buys the same protection and keeps the handle.
    const isNative = Capacitor.isNativePlatform();
    const parkedTab = isNative ? null : window.open('', '_blank');
    // Safe to touch: the tab is still same-origin about:blank until it is navigated.
    if (parkedTab) parkedTab.opener = null;

    try {
      const result = await getInvoiceUrl(orderId, invoiceId);

      if (result.kind === 'ready') {
        if (isNative) {
          await Browser.open({ url: result.sasUrl });
          return;
        }

        if (parkedTab) {
          parkedTab.location.href = result.sasUrl;
          return;
        }

        // The parked tab was blocked before it opened. Worth one direct attempt — some browsers
        // allow this. If that is blocked too, say so rather than letting the tap do nothing.
        if (!window.open(result.sasUrl, '_blank')) {
          showToast(t('App.Labels.ActivityDetailInvoiceError'));
        }
        return;
      }

      parkedTab?.close();
      showToast(
        result.kind === 'pending'
          ? t('App.Labels.ActivityDetailInvoicePending')
          : t('App.Labels.ActivityDetailInvoiceError')
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button
        variant="secondary-outline"
        palette="dark"
        className="w-full"
        loading={loading}
        onClick={handleClick}
      >
        {t('App.Labels.ActivityDetailGetInvoice')}
      </Button>

      <Toast
        key={toastKey}
        open={toast !== null}
        message={toast ?? ''}
        variant="error"
        onDismiss={() => setToast(null)}
      />
    </>
  );
}
